import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import { isFinicityEnabled } from "../../../utils/finicityUtils";
import React, { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import { ICustomerEmployment } from "../../../Interfaces/Customer/ICustomerEmployment";
import { ICustomerIncomeDebt } from "../../../Interfaces/Customer/ICustomerIncomeDebt";
import BuyerInformationForm from "./BuyerInformationForm";
import EmploymentInfoList from "./EmploymentInfoList";
import IncomeDebt from "./IncomeDebt";
import { ICustomer } from "../../../Interfaces/Customer/ICustomer";
import { useDispatch, useSelector } from "react-redux";
import {
  ApplicationDispatch,
  createCustomerReference,
  deleteCustomerReference,
  sendCustomerReferencesToAms,
  updateCustomerReference,
  updateCustomerReferenceVerification,
} from "../../../store/application/types";
import {
  BusinessAction,
  useBusinessRulesEngine,
} from "../../../Data/BusinessRulesData";
import {
  pullCredit,
  updateEditingApp,
} from "../../../store/application/actions";
import { AppState } from '../../../store/rootReducer';
import { IApplication } from "../../../Interfaces/Application/IApplication";
import Tab from "@mui/material/Tab";
import { showValidationDialog } from "../../../utils/ValidationDialog";
import { ICustomerRole } from "../../../Interfaces/Customer/ICustomerRole";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from '@mui/icons-material/Delete';
import { useDialog } from "../../../providers/DialogProvider";
import EmploymentDialog from "../../Dialogs/EmploymentDialog";
import produce from "immer";
import Button from "@mui/material/Button";
import {
  canUpdateApp,
  handleApplicantSwap,
  handleCoApplicantDelete,
  isSalesPerson,
  validateCreditInfo,
  validateFinicityInfo,
} from "../../../utils/appUtils";
import { IAppCustomer } from "../../../Interfaces/Application/IAppCustomer";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useConfiguration } from "../../../Data/ConfigurationData";
import { Constants, permissions } from "../../../utils/constants";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Theme } from "@mui/material/styles";
import ErrorDialog from '../../Dialogs/ErrorDialog';
import { ICustomerReference } from "../../../Interfaces/Customer/ICustomerReference";
import { useEmployeeRolePermissions } from "../../../Data/EmployeeRoleData";
import { ICustomerPreviousCarCredit } from "../../../Interfaces/Customer/ICustomerPreviousCarCredit";
import { v4 } from 'uuid';
import CustomerVehicleInsuranceTab from "./CustomerInsurance";
import { ILoggedInEmployee } from "../../../Interfaces/Admin/ILoggedInEmployee";
import { formatDateOrTimeString, getCustomerPortalLinkMenuOptionsByRole, userHasPermission } from "../../../utils/utils";
import { isNil, isNotNil } from "ramda";
import {
  FilledTemplateSubjectAndBody, hasValue, LinkType,
  LookupItem, MessageTemplates, PermissionSet, SimpleEmailMessage, SimpleMessagingDialog, SimpleTextMessage
} from "@amsanalytics/react-shared-library";
import CreditPullConfirmationDialog from "../../Dialogs/CreditPullConfirmationDialog";
import CustomerReferences from "./CustomerReferences/CustomerReferences";
import { IState } from "../../../Interfaces/Common/IState";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import { generateCustomerPortalFinicityLinkUtil, getFilledTemplate, sendFinicityEmailMessagesToContactUtil, sendFinicityTextMessagesToContactUtil, sendSimpleEmailMessagesToContact, sendSimpleTextMessagesToContact } from "../../../utils/portalLinkUtils";
import { IEmployee } from "../../../Interfaces/Admin/IEmployee";
import { MS_PER_MINUTE } from "@progress/kendo-date-math";
import { getCurrentFincityStatus } from "../../../utils/finicityUtils";
import { ICreateUpdateCustomerVehicleInsurance } from "../../../Interfaces/Customer/IAppCustomerVehicleInsurance";
import { usePostedAppLock } from "../../../hooks/usePostedAppLock";
import LockNotice from "../../Shared/LockNotice";

export interface ApplicantInfoProps {
  appCustomer: IAppCustomer;
  nestedTabNameFlag?: string;
  setNestedTabNameFlag: (arg0: string | undefined) => void;
}

export const ApplicantInfo: React.FC<ApplicantInfoProps> = (props) => {
  const tabsVisible: TabName[] = [
    "Information",
    "Employment",
    "Income and Debt",
    "References"
  ];
  const index = tabsVisible.findIndex((x) => x === props.nestedTabNameFlag);
  const [value, setValue] = useState<number>((index === -1) ? 0 : index);
  const currentApp: IApplication = useSelector(
    (state: AppState) => state.applications.editingApp!
  );
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const states: IState[] = useSelector((state: AppState) => state.applications.states);
  // List of income types the user can select from.
  const incomeTypes: LookupItem[] = useSelector((state: AppState) => state.applications.incomeTypes);
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();
  const [openDialog, closeDialog] = useDialog();
  const [canView, canEdit, canAct] = useEmployeeRolePermissions();
  const { data: configuration } = useConfiguration();
  const isSuperUser = configuration?.isSuperUser
  const activeUser: ILoggedInEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const salesPerson = isSalesPerson(activeUser);
  const salesAppStatus = (currentApp.appStatusId.toUpperCase() === Constants.applicationStatuses.sales ||
    currentApp.appStatusId.toUpperCase() === Constants.applicationStatuses.new);
  const disableEditing = (configuration?.featureFlags?.[FeatureFlagIds.PreventSalesFromEditingApplicant] === true && salesPerson === true && salesAppStatus === false);
  const { isSectionLocked, lockMessage } = usePostedAppLock();
  const customerDetailsLocked = isSectionLocked('CustomerDetails');
  const disableEditingForLock = disableEditing || customerDetailsLocked;
  // Get dimensions of window so we can determine if we are on mobile or desktop.
  const { width } = useWindowDimensions();
  const [isGeneratingCustomerPortalLink, setIsGeneratingCustomerPortalLink] = useState<boolean>(false);
  const employees: IEmployee[] = useSelector((state: AppState) => state.applications.employees);
  const currentEmployee = employees.find((e) => e.id === activeUser.id);
  // Finicity is licensed per client (FinicityEnabled flag); without it the buttons stay hidden and the status poll never starts.
  const finicityEnabled = isFinicityEnabled(configuration);
  const userCanGenerateFinicityLink: boolean = finicityEnabled && isNotNil(currentEmployee) && userHasPermission(activeUser, permissions.finicityBankVerification, PermissionSet.C);
  const userCanViewFinicityReport: boolean = finicityEnabled && isNotNil(currentEmployee) && userHasPermission(activeUser, permissions.finicityBankVerification, PermissionSet.R);
  const [bankVerificationStatusInfo, setbankVerificationStatusInfo] =
    useState<{ primaryStatus: string, coApp1Status: string, coApp2Status: string, primaryStatusDate, coApp1StatusDate, coApp2StatusDate }>
      ({ primaryStatus: "", coApp1Status: "", coApp2Status: "", primaryStatusDate: new Date(), coApp1StatusDate: new Date(), coApp2StatusDate: new Date() });

  // Once when page loads
  useEffect(() => {
    const update = async () => {
      if (userCanViewFinicityReport) {
        const response = await getCurrentFincityStatus(currentApp.id, dispatch);
        const result = response.status;
        setbankVerificationStatusInfo({
          primaryStatus: result.primaryStatus, coApp1Status: result.coApp1Status, coApp2Status: result.coApp2Status,
          primaryStatusDate: result.primaryStatusDate, coApp1StatusDate: result.coApp1StatusDate, coApp2StatusDate: result.coApp2StatusDate
        });
      }
    };
    if (userCanViewFinicityReport) {
      update();
    }
  }, []);

  // Then set a timer to update every 5 mins if they stay on the page
  useEffect(() => {
    let timer = setInterval(async () => {
      if (userCanViewFinicityReport) {
        const response = await getCurrentFincityStatus(currentApp.id, dispatch);
        const result = response.status;
        setbankVerificationStatusInfo({
          primaryStatus: result.primaryStatus, coApp1Status: result.coApp1Status, coApp2Status: result.coApp2Status,
          primaryStatusDate: result.primaryStatusDate, coApp1StatusDate: result.coApp1StatusDate, coApp2StatusDate: result.coApp2StatusDate
        });
      }
    }, 5 * MS_PER_MINUTE); // 300,000 = 5 minutes * 60 seconds * 1000 milliseconds;
    return () => clearInterval(timer);
  }, [bankVerificationStatusInfo, currentApp]);


  // Index of the selected tab so we can display the correct tab content.
  let tabIndex = 0;

  const customer: ICustomer = props.appCustomer.customer;
  const customerRole: ICustomerRole = props.appCustomer.customerRole;
  // Memoization of the props.appCustomer.id to prevent unnecessary re-renders in the CustomerInsurance component
  const appCustomerId = props.appCustomer.id;
  // Memoization of the props.appCustomer.appCustomerVehicleInsurances to prevent unnecessary re-renders in the CustomerInsurance component
  const appCustomerVehicleInsurances = props.appCustomer.appCustomerVehicleInsurances || [];

  props.setNestedTabNameFlag(undefined); //clears it out after it switches the first time


  const handleChange = (
    _event: any,
    newValue: number,
    bypassAppRequirements?: boolean
  ): void => {
    const tabName = tabsVisible[newValue];
    const action = tabNameToAction[tabName];
    const result = action ? businessRulesEngine({ action }) : { errors: [] };
    if (result?.errors?.length && !bypassAppRequirements) {
      showValidationDialog({
        errors: result.errors,
        callback: () => handleChange(_event, newValue, true),
      });
    } else {
      setValue(newValue);
    }
  };

  const usesApplicationScoring = configuration?.featureFlags[FeatureFlagIds.Scienaptic] === true || configuration?.featureFlags[FeatureFlagIds.CBD] === true;

  // Handles the updating of one of the customer's employment information.
  const handleUpdateEmployment = (emp: ICustomerEmployment): void => {
    const updatedApp = produce(currentApp, (currentApp) => {
      // find the customer in the application to update.
      const updatedCustomer: ICustomer | undefined = currentApp.appCustomers.find((ac) => ac.customerId === customer.id)?.customer;

      // Only update employments if the customer exists.
      if (updatedCustomer) {
        updatedCustomer.customerEmployment = updatedCustomer.customerEmployment.map((ce) => {
          if (ce.id === emp.id) {
            return {
              ...emp,
              isCurrent: emp.primaryEmployment ? true : emp.isCurrent,
              endDate: emp.isCurrent ? undefined : emp.endDate,
            }
          }
          return {
            ...ce,
            primaryEmployment: emp.primaryEmployment ? false : ce.primaryEmployment,
          }
        });
      }
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  };

  const handleUpdateIncomeDebt = (incDebt: ICustomerIncomeDebt): void => {
    const index: number = customer?.customerIncomeDebt.findIndex(
      (x) => x.id === incDebt.id
    )!;
    const updatedApp = produce(currentApp, (currentApp) => {
      const updatedCustomer = currentApp.appCustomers.find(
        (ac) => ac.customerId === customer.id
      )?.customer;
      if (updatedCustomer) {
        updatedCustomer.customerIncomeDebt[index] = incDebt;
      }
    });
    // customer.customerIncomeDebt = Object.assign([...customer?.customerIncomeDebt!], { [index]: incDebt });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  };

  const handleUpdatePreviousCarCredit = (previousCarCredit: ICustomerPreviousCarCredit): void => {
    const index: number = customer?.customerPreviousCarCredits.findIndex((cpcc) => cpcc.id === previousCarCredit.id)!;
    const updatedApp = produce(currentApp, (currentApp) => {
      const updatedCustomer = currentApp.appCustomers.find(
        (ac) => ac.customerId === customer.id
      )?.customer;
      if (updatedCustomer) {
        updatedCustomer.customerPreviousCarCredits[index] = previousCarCredit;
      }
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  };

  const handleUpdateIsPreviousCustomer = (isPreviousCustomer: boolean): void => {
    const updatedApp = produce(currentApp, (currentApp) => {
      const updatedCustomer = currentApp.appCustomers.find(
        (ac) => ac.customerId === customer.id
      )?.customer;
      if (updatedCustomer) {
        updatedCustomer.isPreviousCustomer = isPreviousCustomer;
      }
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  };

  const handleHardPullCreditButtonClickSuperUser = (forcePull: boolean): void => {
    const creditValidationErrors: string[] = validateCreditInfo(customer);
    if (creditValidationErrors.length > 0) {
      openDialog({
        children: (
          <ErrorDialog
            title="Credit Validation Errors"
            errors={creditValidationErrors}
            closeDialog={closeDialog}
          />
        ),
      });
    } else {
      openDialog({
        children: (
          <CreditPullConfirmationDialog
            closeDialog={closeDialog}
            prompt={`Are you sure you want to pull credit for ${customer.firstName} ${customer.lastName}?`}
            title="Confirm Hard Credit Pull"
            creditBureaus={configuration!.availableHardPullCreditBureaus}
            pullType="hard"
            handlePullCredit={(creditBureaus: string[]) => {
              dispatch(pullCredit(currentApp.id, customer.id, usesApplicationScoring, forcePull, true, creditBureaus, businessRulesEngine, currentApp.organizationId));
              closeDialog();
            }}
          />
        )
      });
    }
  };

  const handleSoftPullCreditButtonClick = (forcePull: boolean): void => {
    const creditValidationErrors: string[] = validateCreditInfo(customer);
    if (creditValidationErrors.length > 0) {
      openDialog({
        children: (
          <ErrorDialog
            title="Credit Validation Errors"
            errors={creditValidationErrors}
            closeDialog={closeDialog}
          />
        ),
      });
    } else {
      openDialog({
        children: (
          <CreditPullConfirmationDialog
            closeDialog={closeDialog}
            prompt={`Are you sure you want to pull credit for ${customer.firstName} ${customer.lastName}?`}
            title="Confirm Soft Credit Pull"
            creditBureaus={configuration!.availableSoftPullCreditBureaus}
            pullType="soft"
            handlePullCredit={(creditBureaus: string[]) => {
              dispatch(pullCredit(currentApp.id, customer.id, usesApplicationScoring, forcePull, false, creditBureaus, businessRulesEngine, currentApp.organizationId));
              closeDialog();
            }}
          />
        )
      });
    }
  };


  const handleGenerateFinicityLinkButtonClick = (primaryApplicant: boolean): void => {
    const finicityValidationErrors: string[] = validateFinicityInfo(customer);
    if (finicityValidationErrors.length > 0) {
      openDialog({
        children: (
          <ErrorDialog
            title="Finicity Validation Errors"
            errors={finicityValidationErrors}
            closeDialog={closeDialog}
          />
        ),
      });
    } else {
      if (primaryApplicant) {
        openSimpleMessagingForFinicityLinkDialog(LinkType.bankVerificationLinkApplicant);
      }
      else {
        openSimpleMessagingForFinicityLinkDialog(LinkType.bankVerificationLinkCoApplicant);
      }
    }
  };


  const openSimpleMessagingForFinicityLinkDialog = async (linkType: LinkType) => {
    try {
      if (!currentApp) {
        return;
      }

      // set the isGeneratingCustomerPortalLink flag to true so we can show a loading indicator
      setIsGeneratingCustomerPortalLink(true);

      const response: { portalLink: string } | { error: string } = await generateCustomerPortalFinicityLinkUtil(currentApp.contactOpportunityId!, linkType, dispatch)

      // show an error if we were unable to generate a customer portal link
      if (hasValue((response as { error: string }).error)) {
        dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: (response as { error: string }).error, isError: true } });
        return;
      }

      if (!hasValue((response as { portalLink: string }).portalLink)) {
        dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'Unable to generate a Finicity link. Please try again.', isError: true } });
        return;
      }

      const portalLink = (response as { portalLink: string }).portalLink;

      // Gets both the text and email templates filled at the same time
      const [filledEmailTemplateSubjectAndBody, filledTextTemplateSubjectAndBody] = await Promise.all([
        getFilledTemplate(currentApp.contactOpportunityId!, MessageTemplates.continueApplicationEmail, dispatch),
        getFilledTemplate(currentApp.contactOpportunityId!, MessageTemplates.continueApplicationText, dispatch)
      ]);

      (filledEmailTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body =
        ((filledEmailTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body ?? '') + '<p>' + portalLink + '</p>';
      (filledTextTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body =
        ((filledTextTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body ?? '') + '\n\n' + portalLink;

      setIsGeneratingCustomerPortalLink(false);

      const recipientOptions = getCustomerPortalLinkMenuOptionsByRole(currentApp, linkType);

      openDialog({
        children: (
          <SimpleMessagingDialog
            dialogTitle={'Send Bank Authorization Message'}
            onClose={closeDialog}
            selectableRecipients={recipientOptions}
            defaultEmailSubject={(filledEmailTemplateSubjectAndBody as FilledTemplateSubjectAndBody).subject}
            defaultEmailBody={(filledEmailTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body}
            defaultTextMessage={(filledTextTemplateSubjectAndBody as FilledTemplateSubjectAndBody).body}
            onSendEmailMessage={sendFinicityEmailMessagesToContact}
            onSendTextMessage={sendFinicityTextMessagesToContact}
          />
        ),
      });
    } catch (error) {
      dispatch({
        type: "SET_SERVER_MESSAGE",
        payload: { message: error as string, isError: true }
      });
      setIsGeneratingCustomerPortalLink(false);
    }
  };

  const sendFinicityEmailMessagesToContact = (emailMessage: SimpleEmailMessage) => {
    sendFinicityEmailMessagesToContactUtil(currentApp!, emailMessage, dispatch, closeDialog);
  }

  const sendFinicityTextMessagesToContact = (textMessage: SimpleTextMessage) => {
    sendFinicityTextMessagesToContactUtil(currentApp!, textMessage, currentEmployee, dispatch, closeDialog);
  }

  const addEmployment = () => {
    openDialog({
      children: (
        <EmploymentDialog
          accept={(employment: ICustomerEmployment) => {
            const app = produce(currentApp, (currentApp) => {
              employment.primaryEmployment = false;
              const appCustomer = currentApp.appCustomers.find((ac) => ac.customerId === customer.id);
              if (appCustomer) {
                appCustomer.customer.customerEmployment = [
                  ...appCustomer.customer.customerEmployment.map((ce) => {
                    return {
                      ...ce,
                      primaryEmployment: employment.primaryEmployment ? false : ce.primaryEmployment, // If the new employment is primary, set all other employments to not primary.
                    }
                  }),
                  employment,
                ];
              }
            });
            dispatch(updateEditingApp(app, businessRulesEngine));
            closeDialog();
          }}
          isReadOnly={currentApp.areApplicationCustomersEditable !== true}
          closeDialog={closeDialog}
        />
      ),
    });
  };

  // Returns true if the user has permission to pull credit and the client has the Quick Qualify feature flag turned off. Hide the button if the application is read only.
  // Customer must have accepted the credit application terms and conditions if the CustomerWillAcknowledgeCreditApplicationInformation flag is ON.
  const showCreditPullButton = !isNil(configuration)
    && !isNil(activeUser)
    && userHasPermission(activeUser, permissions.canPullCredit, PermissionSet.R)
    && (configuration?.featureFlags?.[FeatureFlagIds.CustomerWillAcknowledgeCreditApplicationInformation] !== true || isNotNil(customer.customerCredit[0]?.acknowledgementDate))
    && configuration?.featureFlags?.[FeatureFlagIds.QuickQualifyCreditPullAvailable] !== true;

  // Returns true if the user has permission to pull credit and the client has the Quick Qualify feature flag turned on. Hide the button if the application is read only.
  // Customer must have accepted the credit application terms and conditions if the CustomerWillAcknowledgeCreditApplicationInformation flag is ON.
  const showCreditHardPullButtons = !isNil(configuration)
    && !isNil(activeUser)
    && userHasPermission(activeUser, permissions.canPullCredit, PermissionSet.R)
    && (configuration?.featureFlags?.[FeatureFlagIds.CustomerWillAcknowledgeCreditApplicationInformation] !== true || isNotNil(customer.customerCredit[0]?.acknowledgementDate))
    && configuration?.featureFlags?.[FeatureFlagIds.QuickQualifyCreditPullAvailable] === true;

  const onPushReferencesToAms = (customerReferences: ICustomerReference[]) => {
    dispatch(sendCustomerReferencesToAms(customerReferences));
  }

  const customerHasBeenPostedInAms = hasValue(customer.amsCustomerNumber);
  const loanNotPostedErrorMessage = 'You must first post the loan in AMS before sending references';

  const sendCustomerReferenceToAms = (references: ICustomerReference[]) => {
    if (!customerHasBeenPostedInAms) {
      openDialog({
        fullScreen: false,
        children: (
          <ErrorDialog
            title='AMS Loan Must Be Posted'
            errors={[loanNotPostedErrorMessage]}
            closeDialog={closeDialog}
          />
        ),
      });
      return;
    }

    onPushReferencesToAms(references);
  }

  const incomeVerificationButtonText = (): string => {
    if (customerRole.isPrimaryApplicant) {
      if (bankVerificationStatusInfo.primaryStatus.length > 0) {
        return ` (${bankVerificationStatusInfo.primaryStatus} ${formatDateOrTimeString(bankVerificationStatusInfo.primaryStatusDate.toLocaleString())})`
      }
    }

    if (customerRole.id.toUpperCase() === Constants.roleCoApplicant1Id.toUpperCase()) {
      if (bankVerificationStatusInfo.coApp1Status.length > 0) {
        return ` (${bankVerificationStatusInfo.coApp1Status} ${formatDateOrTimeString(bankVerificationStatusInfo.coApp1StatusDate.toLocaleString())})`
      }
    }

    if (customerRole.id.toUpperCase() === Constants.roleCoApplicant2Id.toUpperCase()) {
      if (bankVerificationStatusInfo.coApp2Status.length > 0) {
        return ` (${bankVerificationStatusInfo.coApp2Status} ${formatDateOrTimeString(bankVerificationStatusInfo.coApp2StatusDate.toLocaleString())})`
      }
    }
    return ``;
  }
  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: 'calc(100vh - 112px)', overflowY: 'auto', overflowX: 'hidden' }}>
      <AppBar
        position="sticky"
        color="inherit"
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
        }}
        elevation={1}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          aria-label="scrollable auto tabs"
          style={{ display: "flex 0 1" }}
        >
          <Tab
            // icon={<Info />}
            label="Information"
          />
          <Tab
            // icon={<Work />}
            label="Employment"
          />
          <Tab
            // icon={<Money />}
            label="Income and Debt"
          />
          <Tab
            label="References"
          />
          {customerRole?.id?.toLowerCase() === Constants.roleApplicantId.toLowerCase() &&
            <Tab
              label="Vehicle Insurance"
            />
          }
        </Tabs>
        {customerRole &&
          !customerRole.isPrimaryApplicant &&
          canUpdateApp(currentApp, activeUser) &&
          canAct('Actions', `Make Primary Applicant`) && (
            <Button
              style={{
                alignSelf: `center`,
                marginLeft: `1em`,
                maxHeight: `40px`,
                margin: '5px'
              }}
              variant="outlined"
              onClick={() =>
                handleApplicantSwap(
                  props.appCustomer.id,
                  dispatch,
                  currentApp,
                  businessRulesEngine
                )
              }
              color="primary"
            >
              <SwapHorizIcon style={{ marginRight: "5px" }} />
              Make Primary Applicant
            </Button>
          )}
        {customerRole &&
          !customerRole.isPrimaryApplicant &&
          configuration?.featureFlags?.[FeatureFlagIds.AllowCoApplicantDelete] === true &&
          canUpdateApp(currentApp, activeUser) &&
          canAct('Actions', `Make Primary Applicant`) && (
            <Button
              style={{
                alignSelf: `center`,
                marginLeft: `1em`,
                maxHeight: `40px`,
                margin: '5px'
              }}
              variant="outlined"
              onClick={() =>
                handleCoApplicantDelete(
                  props.appCustomer.id,
                  dispatch,
                  currentApp,
                  businessRulesEngine
                )
              }
              color="primary"
            >
              <DeleteIcon style={{ marginRight: "5px" }} />
              Remove Co-Applicant
            </Button>
          )}
        {showCreditPullButton && (
          <Button
            variant="outlined"
            color="primary"
            style={{
              alignSelf: `center`,
              marginLeft: `1em`,
              maxHeight: `40px`,
              margin: '5px'
            }}
            onClick={() => handleHardPullCreditButtonClickSuperUser(false)}
          >
            <CreditCardIcon style={{ marginRight: "5px" }} />
            Pull Credit
          </Button>
        )}
        {(showCreditPullButton || showCreditHardPullButtons) && (isSuperUser || userHasPermission(activeUser, permissions.pullCreditOverride, PermissionSet.R)) && (
          <Button
            variant="outlined"
            color="primary"
            style={{
              alignSelf: `center`,
              marginLeft: `1em`,
              maxHeight: `40px`,
              margin: '5px'
            }}
            onClick={() => handleHardPullCreditButtonClickSuperUser(true)}
          >
            <CreditCardIcon style={{ marginRight: "5px", marginLeft: "5px" }} />
            Force Pull Credit
          </Button>
        )}
        {showCreditHardPullButtons && (
          <>
            <Button
              variant="outlined"
              color="primary"
              style={{
                alignSelf: `center`,
                marginLeft: `1em`,
                maxHeight: `40px`,
                margin: '5px'
              }}
              onClick={() => handleSoftPullCreditButtonClick(false)}
            >
              <CreditCardIcon style={{ marginRight: "5px" }} />
              Soft Pull Credit
            </Button>
            <Button
              variant="outlined"
              color="primary"
              style={{
                alignSelf: `center`,
                marginLeft: `1em`,
                maxHeight: `40px`,
                margin: '5px'
              }}
              onClick={() => handleHardPullCreditButtonClickSuperUser(false)}
            >
              <CreditCardIcon style={{ marginRight: "5px" }} />
              Hard Pull Credit
            </Button>
          </>
        )}
        {userCanViewFinicityReport && (
          <>
            <Button
              variant="outlined"
              color="primary"
              style={{
                alignSelf: `center`,
                marginLeft: `1em`,
                maxHeight: `40px`,
                margin: '5px'
              }}
              onClick={() => handleGenerateFinicityLinkButtonClick(customerRole.isPrimaryApplicant)}
              disabled={!userCanGenerateFinicityLink}
            >
              <AccountBalanceIcon style={{ marginRight: "5px" }} />
              Income Verification <span style={{ textTransform: "none", marginLeft: "5px" }}> {`${incomeVerificationButtonText()}`}</span>
            </Button>
          </>
        )}
      </AppBar>
      <Box p={10} style={{ height: '100%', padding: '10px' }}>
        {isSectionLocked('CustomerDetails') && <LockNotice message={lockMessage} />}
        {value === tabIndex++ && (
          <BuyerInformationForm customerRole={customerRole} disabled={disableEditing} disabledForLock={customerDetailsLocked} isReadOnly={currentApp.areApplicationCustomersEditable !== true} />
        )}
        {value === tabIndex++ && (
          <>
            <div style={{ display: "flex", justifyContent: 'flex-end', margin: '0 0 10px 0' }}>
              <Button onClick={addEmployment} color="primary" variant="outlined">
                <AddIcon />
                Add Employment
              </Button>
            </div>
            <EmploymentInfoList
              onUpdateEmployment={handleUpdateEmployment}
              employmentInfo={customer?.customerEmployment}
              disabled={disableEditingForLock}
              isReadOnly={currentApp.areApplicationCustomersEditable !== true}
            />
          </>
        )}
        {value === tabIndex++ && (
          <IncomeDebt
            incomes={customer?.incomes || []}
            incomeTypes={incomeTypes.map((it) => {
              return { id: it.id, name: it.name };
            })}
            customerId={customer?.id}
            clientName={configuration?.clientName}
            incomeDebt={customer?.customerIncomeDebt[0]}
            isPreviousCustomer={customer?.isPreviousCustomer}
            previousCarCredit={(customer && customer.customerPreviousCarCredits) ? customer.customerPreviousCarCredits[0] : { id: v4() }}
            onUpdateIncomeDebt={handleUpdateIncomeDebt}
            onUpdatePreviousCarCredit={handleUpdatePreviousCarCredit}
            onUpdateIsPreviousCustomer={handleUpdateIsPreviousCustomer}
            disabled={disableEditingForLock}
            isReadOnly={currentApp.areApplicationCustomersEditable !== true}
          />
        )}
        {value === tabIndex++ && (
          <div style={currentApp.areApplicationCustomersEditable !== true ? { display: "flex", flex: 1, height: '100%', pointerEvents: 'none', opacity: 0.7 } : { display: "flex", flex: 1, height: '100%' }}>
            <CustomerReferences
              customerId={customer.id}
              currentUser={activeUser}
              applicationStatusId={currentApp.appStatusId}
              amsCustomerNumber={customer.amsCustomerNumber}
              references={customer.customerReferences}
              customerReferenceRelationships={lookups.customerReferenceRelationships}
              states={states}
              isEditable={false}
              isReadOnly={currentApp.areApplicationCustomersEditable !== true}
              applicationDate={currentApp.applicationDate}
              canPushReferencesFromUiToAms={configuration?.featureFlags[FeatureFlagIds.PushReferencesFromUi] === true && customer.customerReferences.length > 0}
              onCreateReference={(customerReference: ICustomerReference) => {
                dispatch(createCustomerReference(customer.id, customerReference, `${activeUser.firstName} ${activeUser.lastName}`));
              }}
              onUpdateReference={(customerReference: ICustomerReference) => {
                dispatch(updateCustomerReference(customerReference));
              }}
              onDeleteReference={(customerReferenceId: string) => {
                dispatch(deleteCustomerReference(customerReferenceId));
              }}
              onUpdateReferenceVerification={(customerReferenceId: string, isVerified: boolean) => {
                dispatch(updateCustomerReferenceVerification(customerReferenceId, isVerified, `${activeUser.firstName} ${activeUser.lastName}`));
              }}
              onPushAllReferencesToAms={() => sendCustomerReferenceToAms(customer.customerReferences)}
              onPushReferencesToAms={(references: ICustomerReference[]) => sendCustomerReferenceToAms(references)}
              width={width}
            />
          </div>
        )}
        {value === tabIndex++ && customerRole?.id?.toLowerCase() === Constants.roleApplicantId.toLowerCase() && (
          <CustomerVehicleInsuranceTab
            applicationId={currentApp.id}
            customerId={customer.id}
            customerVehicleInsurances={appCustomerVehicleInsurances}
            appCustomerId={appCustomerId}
            onCreate={(customerVehicleInsurance: ICreateUpdateCustomerVehicleInsurance) => {
              // update currentApp with new insurance
              const updatedApp = produce(currentApp, (currentApp) => {
                const appCustomer = currentApp.appCustomers.find((ac) => ac.customerId === customer.id);
                if (appCustomer) {
                  appCustomer.appCustomerVehicleInsurances.push(customerVehicleInsurance);
                }
              });
              dispatch(updateEditingApp(updatedApp, businessRulesEngine));
            }}
            onEdit={(customerVehicleInsurance: ICreateUpdateCustomerVehicleInsurance) => {
              // update currentApp with edited insurance
              const updatedApp = produce(currentApp, (currentApp) => {
                const appCustomer = currentApp.appCustomers.find((ac) => ac.customerId === customer.id);
                if (appCustomer) {
                  const index = appCustomer.appCustomerVehicleInsurances.findIndex((cvi) => cvi.id === customerVehicleInsurance.id);
                  appCustomer.appCustomerVehicleInsurances[index] = customerVehicleInsurance;
                }
              });
              dispatch(updateEditingApp(updatedApp, businessRulesEngine));
            }}
            onDelete={(customerVehicleInsuranceId: string) => {
              // remove the insurance from currentApp
              const updatedApp = produce(currentApp, (currentApp) => {
                const appCustomer = currentApp.appCustomers.find((ac) => ac.customerId === customer.id);
                if (appCustomer) {
                  appCustomer.appCustomerVehicleInsurances = appCustomer.appCustomerVehicleInsurances.filter((cvi) => cvi.id !== customerVehicleInsuranceId);
                }
              });
              dispatch(updateEditingApp(updatedApp, businessRulesEngine));
            }}
          />
        )}
      </Box>
    </div>
  );
};

type TabName = "Information" | "Employment" | "Income and Debt" | "References";

const tabNameToAction: { [key in TabName]: BusinessAction } = {
  Information: "View Information",
  Employment: "View Employment",
  "Income and Debt": "View Income and Debt",
  References: "View References"
};
